import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';

import { useUserDataStore } from '../../newStore/userDataStore';
import { paths } from '../../utils/navigation';
import { useSSOAuthContext } from './SSOAuthProvider';

export function AuthLayer() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentUser = useUserDataStore((state) => state.currentUser);
  const { isSSOAuthenticated, isLoading: ssoLoading } = useSSOAuthContext();

  // Create a clear authentication state
  const isAuthenticated = currentUser?.id || isSSOAuthenticated;

  // Function to merge query parameters from location.search with tracking params
  const buildQueryString = (basePath: string) => {
    const allParams = new URLSearchParams(location.search);
    const paramsToPreserve = ['__hstc', '__hssc', '__hsfp', '_gl'];

    // Merge tracking params only if they're missing from location.search
    paramsToPreserve.forEach((param) => {
      if (!allParams.has(param) && searchParams.has(param)) {
        allParams.append(param, searchParams.get(param)!);
      }
    });

    const queryString = allParams.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  const getSafeReturnTo = () => {
    const returnTo = searchParams.get('returnTo');
    if (!returnTo || !returnTo.startsWith('/')) {
      return null;
    }

    return returnTo;
  };

  // Check if user needs to be redirected to onboarding but is not already there
  // Only regular users (not SSO) need onboarding, and they must be superadmin
  // But we need to allow login if they are trying to access another user
  const userNeedsOnboarding = currentUser?.id && !currentUser?.onboarding_completed && currentUser?.is_superadmin;
  const isProtectedPathFromOnboarding = location.pathname.startsWith(paths.onboarding.index) || location.pathname === paths.home.index;
  if (userNeedsOnboarding && !isProtectedPathFromOnboarding) {
    return <Navigate to={buildQueryString(paths.onboarding.index)} />;
  }

  // Non-logged in users should not access onboarding
  if (!isAuthenticated && location.pathname.startsWith(paths.onboarding.index)) {
    return <Navigate to={buildQueryString(paths.auth.signUp)} />;
  }

  // Logged in users should go to home unless they're on set_profile or onboarding
  if (
    isAuthenticated &&
    !location.pathname.startsWith('/set_profile') &&
    !location.pathname.startsWith(paths.onboarding.index) &&
    !location.pathname.startsWith('/auth/cognito/callback')
  ) {
    const authPaths = [
      paths.auth.signUp,
      paths.auth.login,
      paths.auth.forgotPassword,
      paths.auth.resetPassword,
    ];
    const isAuthPath = authPaths.includes(location.pathname);
    const safeReturnTo = getSafeReturnTo();

    if (isAuthPath && safeReturnTo) {
      return <Navigate to={safeReturnTo} replace />;
    }

    return <Navigate to={paths.home.index} />;
  }

  return <Outlet />;
}
